import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Save, Eye, Edit3, ArrowLeft, ExternalLink, Sparkles, LayoutTemplate, AlertTriangle, Search, ChevronDown, ChevronUp, Image, EyeOff, ImageIcon, X, Smartphone, Tablet, Monitor, LogOut } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import api from '../utils/api';
import { extractBase64Images } from '../utils/extractBase64Images';
import Sidebar from '../components/Sidebar';
import BlockEditor from '../components/BlockEditor';
import PreviewRenderer from '../components/PreviewRenderer';
import AIDesignerModal from '../components/AIDesignerModal';
import PageThemePanel from '../components/PageThemePanel';
import SaveAsTemplateModal from '../components/SaveAsTemplateModal';
import TemplateSelectionModal from '../components/TemplateSelectionModal';
import MediaPickerModal from '../components/MediaPickerModal';
import SocialPreview from '../components/SocialPreview';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';

// Mirror of the backend og:image fallback — find the first usable image in a
// page's blocks (hero/image/image+text/gallery/columns) so the Social Preview
// matches what social crawlers actually receive on the live page.
const looksLikeImageUrl = (v) =>
  typeof v === 'string' && /^(https?:\/\/|\/)/.test(v) &&
  /(\.jpg|\.jpeg|\.png|\.webp|\.gif|\.svg|\.avif|\/uploads\/|\/api\/uploads)/i.test(v);

const firstBlockImage = (blocks) => {
  if (!Array.isArray(blocks)) return '';
  const priority = ['imageUrl', 'image_url', 'backgroundImage', 'imageSrc', 'src', 'url'];
  const scan = (obj) => {
    if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
      for (const k of priority) if (looksLikeImageUrl(obj[k])) return obj[k];
      for (const val of Object.values(obj)) { const r = scan(val); if (r) return r; }
    } else if (Array.isArray(obj)) {
      for (const val of obj) { const r = scan(val); if (r) return r; }
    }
    return '';
  };
  for (const b of blocks) {
    const content = b?.content || b;
    if (content?.isPublished === false) continue;
    const r = scan(content);
    if (r) return r;
  }
  return '';
};

// Utility to strip inline color styles when using site theme
const stripInlineColorStyles = (html, shouldStrip) => {
  if (!shouldStrip || !html) return html;
  return html.replace(/style="([^"]*)"/gi, (match, styleContent) => {
    const cleanedStyle = styleContent
      .split(';')
      .filter(prop => {
        const trimmed = prop.trim().toLowerCase();
        return !trimmed.startsWith('color:') && 
               !trimmed.startsWith('background-color:') &&
               !trimmed.startsWith('background:');
      })
      .join(';')
      .trim();
    if (!cleanedStyle) return '';
    return `style="${cleanedStyle}"`;
  });
};

const PageEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isNew = id === 'new';
  const templateId = searchParams.get('template');

  const [pageData, setPageData] = useState({
    title: '',
    slug: '',
    blocks: [],
    status: 'draft',
    pageWidth: 'standard', // narrow, standard, wide, full
    theme: { use_site_theme: true }, // Page-level theme settings
    parent_id: null, // Parent page for hierarchical URLs
    // SEO fields
    meta_description: '',
    og_image: '',
    og_title: '',
    og_description: '',
    noindex: false,
    canonical_url: '',
  });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  // When previewing, authors can simulate a phone (375px) or tablet (768px)
  // viewport so they can verify sliders / columns / typography without
  // deploying. 'desktop' = inline render (existing). Mobile/Tablet renders
  // inside an iframe so the inner viewport is actually that width — Tailwind
  // `md:` breakpoints and CSS `vw` units evaluate against the iframe's own
  // dimensions, producing a faithful device preview.
  const [previewViewport, setPreviewViewport] = useState('desktop');
  // Cached preview URL for the iframe; refreshed on entry to mobile/tablet
  // mode so the iframe always shows the latest saved blocks.
  const [previewIframeUrl, setPreviewIframeUrl] = useState(null);
  const [iframeLoading, setIframeLoading] = useState(false);
  const [showAIDesigner, setShowAIDesigner] = useState(false);
  const { user, logout } = useAuth();  // AI Design is hidden from everyone except Super Admins for now —
  // will be re-enabled to all users once the feature is improved.
  const canUseAIDesigner = user?.role === 'super_admin';
  const [showSaveAsTemplate, setShowSaveAsTemplate] = useState(false);
  const [showTemplateSelection, setShowTemplateSelection] = useState(false);
  const [siteTheme, setSiteTheme] = useState(null);
  const [tenantBranding, setTenantBranding] = useState(null); // {logo_url, favicon_url} — for SocialPreview fallbacks
  const [previewRefreshKey, setPreviewRefreshKey] = useState(0);
  const [allPages, setAllPages] = useState([]); // For internal page linking
  const [availableParentPages, setAvailableParentPages] = useState([]); // For parent page selector
  const [allPagesAnchors, setAllPagesAnchors] = useState([]); // Anchors from all pages
  const [showSeoPanel, setShowSeoPanel] = useState(false); // SEO panel expanded/collapsed
  const [showOgImagePicker, setShowOgImagePicker] = useState(false); // Social preview media picker
  
  // Unsaved changes tracking
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const initialLoadComplete = useRef(false);
  const pendingNavigationPath = useRef(null);
  const hasUnsavedChangesRef = useRef(false);

  // Keep ref in sync with state for use in event handlers
  useEffect(() => {
    hasUnsavedChangesRef.current = hasUnsavedChanges;
  }, [hasUnsavedChanges]);

  // Warn before browser close/refresh
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChangesRef.current) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Intercept clicks on navigation links
  useEffect(() => {
    const handleClick = (e) => {
      // Only intercept if there are unsaved changes
      if (!hasUnsavedChangesRef.current) return;
      
      // Find if click is on an anchor tag or inside one
      const link = e.target.closest('a[href]');
      if (!link) return;
      
      const href = link.getAttribute('href');
      // Only intercept internal navigation (not external links)
      if (!href || href.startsWith('http') || href.startsWith('mailto:') || href.startsWith('#')) return;
      
      // Prevent navigation and show modal
      e.preventDefault();
      e.stopPropagation();
      pendingNavigationPath.current = href;
      setShowUnsavedModal(true);
    };

    // Capture phase to intercept before React Router
    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, []);

  useEffect(() => {
    fetchSiteTheme();
    fetchAllPages(); // Fetch pages for link selector
    if (!isNew) {
      fetchPage();
    } else if (templateId) {
      // Load template if specified in URL
      loadTemplate(templateId);
    } else {
      // Show template selection modal for new pages
      setShowTemplateSelection(true);
    }
  }, [id, templateId]);

  // Fetch all pages for internal linking dropdown and parent page selection
  const fetchAllPages = async () => {
    try {
      const response = await api.get('/pages');
      const pagesData = response.data;
      
      // Extract basic page info for page selection
      setAllPages(pagesData.map(p => ({
        page_id: p.page_id,
        slug: p.slug,
        title: p.title,
        full_path: p.full_path
      })));
      
      // Set available parent pages (exclude current page and its children to prevent circular references)
      const currentPageId = id !== 'new' ? id : null;
      setAvailableParentPages(pagesData.filter(p => {
        // Exclude current page
        if (currentPageId && p.page_id === currentPageId) return false;
        // Exclude popups (they can't be parents)
        if (p.isPopup) return false;
        return true;
      }).map(p => ({
        page_id: p.page_id,
        slug: p.slug,
        title: p.title || p.slug,
        full_path: p.full_path,
        parent_id: p.parent_id
      })));
      
      // Extract anchors from all pages' blocks
      const allAnchors = [];
      pagesData.forEach(page => {
        const pageBlocks = page.blocks || [];
        pageBlocks.forEach(block => {
          // Check if block has an anchor_id or is a type that typically has anchors
          if (block.content?.anchor_id || ['heading', 'hero', 'form', 'gallery', 'article-feed'].includes(block.type)) {
            let name = block.type.charAt(0).toUpperCase() + block.type.slice(1);
            if (block.type === 'heading' && block.content?.text) {
              name = block.content.text.substring(0, 30);
            } else if (block.type === 'heading' && block.content?.html) {
              name = block.content.html.replace(/<[^>]*>/g, '').substring(0, 30);
            }
            
            // Generate anchor ID if not explicitly set
            const anchorId = block.content?.anchor_id || 
              `${block.type}-${block.id?.substring(0, 8) || Math.random().toString(36).substr(2, 8)}`;
            
            allAnchors.push({
              id: anchorId,
              name: name,
              pageSlug: page.slug,
              pageTitle: page.title
            });
          }
        });
      });
      
      setAllPagesAnchors(allAnchors);
    } catch (err) {
      console.error('Failed to fetch pages for linking:', err);
    }
  };

  // Resolve the theme a new page should start with when a template is loaded.
  //
  // Historical bug: templates shipped by vision imports (and many older
  // system templates) had `theme.use_site_theme = false` baked into their
  // record along with a fixed palette. Copying that theme verbatim onto a
  // new page meant the page ignored the tenant's Site Theme forever — even
  // after the tenant switched palettes — because the page's own colours
  // won. The Page Theme panel then showed "Use Site Theme = OFF" the moment
  // the page was created from a template.
  //
  // Rule going forward: a new page defaults to `use_site_theme = true`
  // (follow the Site Theme). We only respect a locked palette when the
  // template ITSELF opts in via `locks_theme = true` (used for AI-vision
  // imports that ship a bespoke palette they want preserved).
  const resolveTemplateTheme = (template) => {
    if (template && template.locks_theme === true) {
      return { ...(template.theme || {}), use_site_theme: false };
    }
    return { use_site_theme: true };
  };

  const loadTemplate = async (tplId) => {
    try {
      const res = await api.get(`/templates/${tplId}`);
      const template = res.data;
      setPageData({
        ...pageData,
        blocks: template.blocks || [],
        pageWidth: template.page_width || 'standard',
        theme: resolveTemplateTheme(template),
      });
      toast.success(`Loaded template: ${template.name}`);
      // Mark initial load complete after template loads
      setTimeout(() => {
        initialLoadComplete.current = true;
      }, 100);
    } catch (error) {
      console.error('Failed to load template:', error);
      toast.error('Failed to load template');
    }
  };

  const handleSelectTemplate = (template) => {
    setPageData({
      ...pageData,
      blocks: template.blocks || [],
      pageWidth: template.page_width || 'standard',
      theme: resolveTemplateTheme(template),
    });
    toast.success(`Loaded template: ${template.name}`);
    // Mark initial load complete after template selection
    setTimeout(() => {
      initialLoadComplete.current = true;
    }, 100);
  };

  const fetchSiteTheme = async () => {
    try {
      const response = await api.get('/theme');
      setSiteTheme(response.data);
    } catch (error) {
      console.error('Failed to fetch site theme');
    }
    // Also fetch branding (logo + favicon) — used by SocialPreview as the
    // og:image fallback when no page-specific image is set.
    try {
      const res = await api.get('/tenant/branding');
      setTenantBranding(res.data || null);
    } catch (e) {
      /* non-fatal — preview just won't have a fallback image */
    }
  };

  const fetchPage = async () => {
    try {
      const response = await api.get(`/pages/${id}`);
      setPageData(response.data);
      // Mark initial load complete so we can start tracking changes
      setTimeout(() => {
        initialLoadComplete.current = true;
      }, 100);
    } catch (error) {
      toast.error('Failed to load page');
      navigate('/pages');
    } finally {
      setLoading(false);
    }
  };

  // Track changes to pageData after initial load
  const updatePageData = useCallback((updater) => {
    setPageData(prev => {
      const newData = typeof updater === 'function' ? updater(prev) : updater;
      if (initialLoadComplete.current) {
        setHasUnsavedChanges(true);
      }
      return newData;
    });
  }, []);

  const handleSave = async () => {
    if (!pageData.slug) {
      toast.error('Please enter a page slug');
      return;
    }

    setSaving(true);
    try {
      // Move any base64 images embedded in blocks into the Media Library so the
      // saved payload stays small (prevents the proxy from rejecting big bodies).
      let dataToSave = pageData;
      const { blocks: cleanedBlocks, uploaded } = await extractBase64Images(pageData.blocks);
      if (uploaded > 0) {
        dataToSave = { ...pageData, blocks: cleanedBlocks };
        setPageData(dataToSave);
        toast.info(`Optimized ${uploaded} embedded image${uploaded > 1 ? 's' : ''} to the Media Library`);
      }
      if (isNew) {
        const response = await api.post('/pages', dataToSave);
        toast.success('Page created successfully');
        setHasUnsavedChanges(false);
        navigate(`/pages/${response.data.page_id}`);
      } else {
        await api.put(`/pages/${id}`, dataToSave);
        toast.success('Page updated successfully');
        setHasUnsavedChanges(false);
      }
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save page');
    } finally {
      setSaving(false);
    }
  };

  // Handle unsaved changes modal actions
  const handleDiscardChanges = () => {
    setHasUnsavedChanges(false);
    setShowUnsavedModal(false);
    if (pendingNavigationPath.current) {
      navigate(pendingNavigationPath.current);
      pendingNavigationPath.current = null;
    }
  };

  const handleCancelNavigation = () => {
    setShowUnsavedModal(false);
    pendingNavigationPath.current = null;
  };

  const handleSaveAndNavigate = async () => {
    await handleSave();
    setShowUnsavedModal(false);
    if (pendingNavigationPath.current) {
      navigate(pendingNavigationPath.current);
      pendingNavigationPath.current = null;
    }
  };

  const handleAIBlocksGenerated = async (newBlocks) => {
    // Append blocks to page
    const updatedBlocks = [...pageData.blocks, ...newBlocks];
    updatePageData(prev => ({
      ...prev,
      blocks: updatedBlocks
    }));

    // Auto-save after AI generation
    toast.info('AI blocks added. Saving...');
    
    try {
      if (isNew) {
        // For new pages, we need a slug first
        if (!pageData.slug) {
          toast.warning('Please add a URL slug and save the page');
          return;
        }
        const response = await api.post('/pages', {
          ...pageData,
          blocks: updatedBlocks
        });
        toast.success('Page created with AI design!');
        navigate(`/pages/${response.data.page_id}`);
      } else {
        await api.put(`/pages/${id}`, {
          ...pageData,
          blocks: updatedBlocks
        });
        toast.success('AI design saved!');
      }
    } catch (error) {
      toast.error('Auto-save failed. Please click Save manually.');
    }
  };

  const generateSlug = (text) => {
    return text.toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/^-+|-+$/g, '').replace(/-+/g, '-');
  };

  const getPageWidthClass = (width) => {
    switch (width) {
      case 'narrow': return 'max-w-3xl'; // 720px
      case 'standard': return 'max-w-5xl'; // 1024px
      case 'wide': return 'max-w-7xl'; // 1280px
      case 'full': return 'max-w-full';
      default: return 'max-w-5xl';
    }
  };

  // Get effective theme value for full preview
  const getThemeValue = (key) => {
    const pt = pageData.theme;
    if (pt && pt.use_site_theme === false && pt[key]) {
      return pt[key];
    }
    return siteTheme?.[key];
  };

  // Get page background for full preview
  const getPreviewBackground = () => {
    const bgType = getThemeValue('background_type') || 'solid';
    if (bgType === 'linear-gradient') {
      return `linear-gradient(${getThemeValue('gradient_direction') || 'to bottom'}, ${getThemeValue('gradient_start') || '#6366f1'}, ${getThemeValue('gradient_end') || '#8b5cf6'})`;
    } else if (bgType === 'radial-gradient') {
      return `radial-gradient(circle, ${getThemeValue('gradient_start') || '#6366f1'}, ${getThemeValue('gradient_end') || '#8b5cf6'})`;
    }
    return getThemeValue('background_color') || '#ffffff';
  };

  const openFullPreview = async () => {
    // For new pages that haven't been saved yet, show a message
    if (isNew) {
      toast.error('Please save the page first to enable Full Preview');
      return;
    }
    
    try {
      // First, save the current changes so preview shows latest content
      if (pageData.blocks.length > 0) {
        await handleSave();
      }
      
      // Get or generate preview token
      const response = await api.get(`/pages/${id}/preview-token`);
      const { preview_token } = response.data;
      
      // Open preview in new tab
      const previewUrl = `${window.location.origin}/preview/${preview_token}`;
      window.open(previewUrl, '_blank');
      
      toast.success('Preview opened in new tab');
    } catch (error) {
      console.error('Failed to open preview:', error);
      toast.error('Failed to open preview. Please try saving the page first.');
    }
  };

  // Switch the preview viewport. For mobile/tablet, save the page (once)
  // and fetch a preview token so the iframe shows the latest content at the
  // simulated narrow viewport. Imperative — NOT in a useEffect — so a busy
  // re-render storm (handleSave updates pageData → re-render → effect
  // re-runs) can't fire repeat saves. Earlier bug: 100+ "page updated
  // successfully" toasts when entering mobile preview.
  const handleSwitchViewport = async (viewport) => {
    setPreviewViewport(viewport);
    if (viewport === 'desktop') return;
    if (previewIframeUrl) return; // already loaded; just switch wrapper width
    if (isNew) {
      toast.error('Please save the page first to use mobile preview');
      return;
    }
    setIframeLoading(true);
    try {
      if (pageData.blocks && pageData.blocks.length > 0) {
        await handleSave();
      }
      const response = await api.get(`/pages/${id}/preview-token`);
      const { preview_token } = response.data;
      setPreviewIframeUrl(`${window.location.origin}/preview/${preview_token}?t=${Date.now()}`);
    } catch (err) {
      console.error('Failed to build iframe preview URL:', err);
      toast.error('Could not load mobile preview. Try saving the page first.');
    } finally {
      setIframeLoading(false);
    }
  };

  // Manual refresh — bypasses the "already loaded" guard so it always
  // re-saves and re-fetches a fresh URL. Can't just clear `previewIframeUrl`
  // and call handleSwitchViewport because the closure reads the stale value.
  const refreshIframePreview = async () => {
    if (isNew) {
      toast.error('Please save the page first to use mobile preview');
      return;
    }
    setPreviewIframeUrl(null);
    setIframeLoading(true);
    try {
      if (pageData.blocks && pageData.blocks.length > 0) {
        await handleSave();
      }
      const response = await api.get(`/pages/${id}/preview-token`);
      setPreviewIframeUrl(`${window.location.origin}/preview/${response.data.preview_token}?t=${Date.now()}`);
    } catch (err) {
      console.error('Failed to refresh preview:', err);
      toast.error('Could not refresh preview.');
    } finally {
      setIframeLoading(false);
    }
  };

  const renderBlocksToHtml = (blocks) => {
    // Check if site theme is being used
    const usingSiteTheme = !pageData.theme || pageData.theme.use_site_theme !== false;
    
    // Helper to process HTML - strips inline colors when using site theme
    const processHtml = (html) => stripInlineColorStyles(html, usingSiteTheme);
    
    return blocks.map((block, index) => {
      const { type, content, styles } = block;
      const safeStyles = styles || {};
      
      // Check if block should be displayed as a card
      const isCard = safeStyles.showAsCard === true;
      
      // Animation style
      const animationStyle = safeStyles.animation && safeStyles.animation !== 'none' 
        ? `animation: ${safeStyles.animation} ${safeStyles.animationDuration || '0.5s'} ease-out ${safeStyles.animationDelay || '0s'} both;`
        : '';
      
      // Card shadow style
      const cardShadow = isCard && !safeStyles.shadow 
        ? 'box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1);' 
        : '';
      
      const blockStyle = `
        background-color: ${isCard ? (safeStyles.backgroundColor || '#ffffff') : (safeStyles.backgroundColor || 'transparent')};
        border-color: ${safeStyles.borderColor || (isCard ? '#e2e8f0' : 'transparent')};
        border-width: ${safeStyles.borderWidth || (isCard ? '1px' : '0px')};
        border-style: ${(safeStyles.borderWidth && safeStyles.borderWidth !== '0px') || isCard ? 'solid' : 'none'};
        border-radius: ${safeStyles.borderRadius || (isCard ? '12px' : '0px')};
        padding: ${safeStyles.padding || (isCard ? '24px' : '0px')};
        ${cardShadow}
        ${animationStyle}
      `;

      switch (type) {
        case 'heading':
          return `<div class="mb-6" style="${blockStyle}">${processHtml(content.html || `<h2>${content.text || 'Heading'}</h2>`)}</div>`;
        
        case 'paragraph':
          return `<div class="mb-4 text-lg leading-relaxed" style="${blockStyle}">${processHtml(content.html || '<p>Start typing...</p>')}</div>`;
        
        case 'image':
          // Support both content.url and content.imageUrl for backward compatibility
          const imageUrl = content.url || content.imageUrl;
          if (!imageUrl) return '';
          const imageSizeMode = safeStyles.imageSizeMode || 'width';
          const imageWidth = safeStyles.imageWidth || '100%';
          const imageHeight = safeStyles.imageHeight || '300px';
          const imageAlign = safeStyles.imageAlign || 'center';
          const isFullWidth = content.fullWidth === true;
          const imageAlt = content.alt || content.imageAlt || '';
          
          if (isFullWidth) {
            return `
              <div class="mb-8 full-width-banner" style="text-align: center; margin-left: calc(-50vw + 50%); margin-right: calc(-50vw + 50%); width: 100vw;">
                <img src="${imageUrl}" alt="${imageAlt}" class="w-full shadow-md" style="width: 100%; height: auto; max-height: 600px; object-fit: cover;" />
                ${content.caption ? `<p class="text-sm opacity-70 mt-3 text-center italic">${content.caption}</p>` : ''}
              </div>
            `;
          }
          
          const imgStyle = imageSizeMode === 'height' 
            ? `width: auto; height: ${imageHeight}; max-width: 100%;`
            : `width: ${imageWidth}; height: auto; max-width: 100%;`;
          return `
            <div class="mb-8" style="${blockStyle}; text-align: ${imageAlign}">
              <img src="${imageUrl}" alt="${imageAlt}" class="rounded-lg shadow-md inline-block" style="${imgStyle}" />
              ${content.caption ? `<p class="text-sm opacity-70 mt-3 text-center italic">${content.caption}</p>` : ''}
            </div>
          `;
        
        case 'quote':
          return `<blockquote class="border-l-4 border-indigo-600 pl-6 py-4 my-6 italic text-xl rounded-r-lg" style="${blockStyle}">${content.text || ''}</blockquote>`;
        
        case 'list':
          const listItems = content.items?.map(item => `<li>${item}</li>`).join('') || '';
          return `<ul class="mb-6 space-y-2 list-disc list-inside text-lg" style="${blockStyle}">${listItems}</ul>`;
        
        case 'code':
          return `
            <div class="mb-6" style="${blockStyle}">
              <div class="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto">
                ${content.language ? `<div class="text-xs text-slate-400 mb-2">${content.language}</div>` : ''}
                <pre class="font-mono text-sm"><code>${content.code || ''}</code></pre>
              </div>
            </div>
          `;
        
        case 'columns':
          const columnCount = content.columnCount || 2;
          const columns = content.columns || [];
          const showAsCards = content.showAsCards || false;
          const cardStyle = content.cardStyle || {};
          const cardAnimation = showAsCards ? (cardStyle.animation || 'none') : 'none';
          const cardStagger = showAsCards && cardStyle.stagger !== false;
          
          const columnsHtml = columns.map((col, colIndex) => {
            // Card animation style - use per-card animation or fall back to default
            const effectiveAnimation = col.animation || cardAnimation;
            const cardAnimationStyle = effectiveAnimation !== 'none' 
              ? `animation: ${effectiveAnimation} 0.5s ease-out ${cardStagger ? `${colIndex * 0.15}s` : '0s'} both;`
              : '';
            
            // Card styling when showAsCards is enabled
            const cardClasses = showAsCards 
              ? `bg-white rounded-lg ${cardStyle.shadow !== false ? 'shadow-md' : ''}`
              : '';
            const cardInlineStyle = showAsCards 
              ? `background-color: ${cardStyle.backgroundColor || '#ffffff'}; border-radius: ${cardStyle.borderRadius || '8px'}; padding: ${cardStyle.padding || '16px'}; ${cardAnimationStyle}`
              : '';
            
            let colContent = '';
            if (col.type === 'text') {
              colContent = `<div class="text-lg leading-relaxed text-slate-700" style="text-align: ${col.textAlign || 'left'}">${processHtml(col.html || '<p>Column content</p>')}</div>`;
            } else if (col.type === 'image' && col.imageUrl) {
              const imgWidth = col.imageSizeMode === 'height' ? 'auto' : `${col.imageWidth || 200}px`;
              const imgHeight = col.imageSizeMode === 'height' ? `${col.imageHeight || 200}px` : 'auto';
              colContent = `
                <div style="text-align: ${col.imageAlign || 'center'}">
                  <img src="${col.imageUrl}" alt="${col.imageAlt || ''}" class="rounded-lg shadow-md inline-block" style="width: ${imgWidth}; height: ${imgHeight}; max-width: 100%;" />
                  ${col.imageAlt ? `<p class="text-sm text-slate-500 mt-2 italic">${col.imageAlt}</p>` : ''}
                </div>
              `;
            }
            
            return showAsCards 
              ? `<div class="${cardClasses}" style="${cardInlineStyle}">${colContent}</div>`
              : `<div>${colContent}</div>`;
          }).join('');
          return `<div class="grid gap-8 mb-8" style="grid-template-columns: repeat(${columnCount}, 1fr); ${blockStyle}">${columnsHtml}</div>`;
        
        case 'two-column':
          return `
            <div class="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <div class="text-lg leading-relaxed text-slate-700">${processHtml(content.leftHtml || '<p>Left column</p>')}</div>
              <div class="text-lg leading-relaxed text-slate-700">${processHtml(content.rightHtml || '<p>Right column</p>')}</div>
            </div>
          `;
        
        case 'hero':
          const heroImageUrl = content.imageUrl || content.backgroundImage || '';
          const heroTitle = content.title || '';
          const heroSubtitle = content.subtitle || '';
          const heroButtonText = content.buttonText || '';
          const heroHeight = content.height || '500px';
          const heroWidth = content.width || 'full';
          const heroWidthClass = heroWidth === 'contained' ? 'max-w-5xl mx-auto rounded-xl overflow-hidden' : 'w-full';
          const textColor = content.textColor === 'black' ? '#1e293b' : '#ffffff';
          const overlayStyle = content.overlay || 'dark';
          const overlayBg = overlayStyle === 'dark' ? 'rgba(0,0,0,0.5)' : overlayStyle === 'light' ? 'rgba(255,255,255,0.5)' : 'transparent';
          
          return `
            <div class="mb-8 ${heroWidthClass}" style="position: relative; min-height: ${heroHeight}; background-image: url('${heroImageUrl}'); background-size: cover; background-position: center; ${blockStyle}">
              <div style="position: absolute; inset: 0; background: ${overlayBg};"></div>
              <div style="position: relative; z-index: 10; display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; padding: 4rem 2rem; min-height: ${heroHeight}; color: ${textColor};">
                ${heroTitle ? `<h1 class="text-5xl font-bold mb-4">${heroTitle}</h1>` : ''}
                ${heroSubtitle ? `<p class="text-xl mb-6 opacity-90">${heroSubtitle}</p>` : ''}
                ${heroButtonText ? `<button class="px-8 py-3 bg-white text-slate-900 rounded-lg font-medium hover:bg-slate-100 transition-colors">${heroButtonText}</button>` : ''}
              </div>
            </div>
          `;
        
        case 'image-text-row':
          const rowImageUrl = content.imageUrl || '';
          const rowImagePosition = content.imagePosition || 'left';
          const rowTextHtml = processHtml(content.textHtml || '<p>Text content</p>');
          const rowImageFirst = rowImagePosition === 'left';
          
          return `
            <div class="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8 items-center" style="${blockStyle}">
              ${rowImageFirst && rowImageUrl ? `<div><img src="${rowImageUrl}" alt="${content.imageAlt || ''}" class="rounded-lg shadow-md w-full h-auto" /></div>` : ''}
              <div class="text-lg leading-relaxed">${rowTextHtml}</div>
              ${!rowImageFirst && rowImageUrl ? `<div><img src="${rowImageUrl}" alt="${content.imageAlt || ''}" class="rounded-lg shadow-md w-full h-auto" /></div>` : ''}
            </div>
          `;
        
        case 'image-text-stack':
          const stackImageUrl = content.imageUrl || '';
          const stackImagePosition = content.imagePosition || 'top';
          const stackTextHtml = processHtml(content.textHtml || '<p>Text content</p>');
          const stackImageTop = stackImagePosition === 'top';
          
          return `
            <div class="mb-8" style="${blockStyle}">
              ${stackImageTop && stackImageUrl ? `<div class="mb-6"><img src="${stackImageUrl}" alt="${content.imageAlt || ''}" class="rounded-lg shadow-md w-full h-auto" /></div>` : ''}
              <div class="text-lg leading-relaxed">${stackTextHtml}</div>
              ${!stackImageTop && stackImageUrl ? `<div class="mt-6"><img src="${stackImageUrl}" alt="${content.imageAlt || ''}" class="rounded-lg shadow-md w-full h-auto" /></div>` : ''}
            </div>
          `;
        
        case 'form':
          // Forms are rendered as placeholders in full preview
          return `
            <div class="mb-8 p-6 bg-slate-50 border border-slate-200 rounded-lg text-center" style="${blockStyle}">
              <p class="text-slate-500">[Form: ${content.formName || content.formId || 'Embedded Form'}]</p>
              <p class="text-xs text-slate-400 mt-1">Forms are interactive on the live page</p>
            </div>
          `;
        
        default:
          return '';
      }
    }).join('');
  };

  if (loading) {
    return (
      <div className="flex">
        <Sidebar />
        <div className="flex-1 bg-white p-8 flex items-center justify-center">
          <p className="text-slate-500">Loading page...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex" data-testid="page-editor">
      <Sidebar />
      
      <div className="flex-1 min-w-0 bg-white">
        <div className="border-b border-slate-200 px-8 py-4 bg-white sticky top-0 z-10">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/pages')} data-testid="back-button">
                <ArrowLeft size={20} />
              </Button>
            </div>
            
            <div className="flex items-center gap-3">
              {/* AI Designer Button — Super Admin only (temporarily hidden from
                  other users while we improve the feature) */}
              {canUseAIDesigner && (
                <Button
                  variant="outline"
                  onClick={() => setShowAIDesigner(true)}
                  className="border-purple-300 text-purple-600 hover:bg-purple-50"
                  data-testid="ai-designer-button"
                >
                  <Sparkles size={18} className="mr-2" />
                  AI Design
                </Button>
              )}

              {/* Save as Template Button */}
              <Button
                variant="outline"
                onClick={() => setShowSaveAsTemplate(true)}
                disabled={pageData.blocks.length === 0}
                title="Save this page as a reusable template"
                data-testid="save-template-button"
              >
                <LayoutTemplate size={18} className="mr-2" />
                Save as Template
              </Button>

              {/* Quick Preview Toggle */}
              <Button
                variant={previewMode ? 'default' : 'outline'}
                onClick={() => {
                  if (!previewMode) {
                    // Entering preview mode - refresh theme data
                    setPreviewRefreshKey(prev => prev + 1);
                    // Clear cached iframe URL so a fresh save+load runs when
                    // user picks mobile/tablet, reflecting their latest edits.
                    setPreviewIframeUrl(null);
                  }
                  setPreviewMode(!previewMode);
                }}
                data-testid="preview-toggle"
              >
                {previewMode ? (
                  <>
                    <Edit3 size={18} className="mr-2" />
                    Edit
                  </>
                ) : (
                  <>
                    <Eye size={18} className="mr-2" />
                    Quick Preview
                  </>
                )}
              </Button>

              {/* Viewport switcher — only meaningful in preview mode.
                  Lets authors verify how the page renders at common device
                  widths without leaving the editor. Mobile = 375px (iPhone),
                  Tablet = 768px (iPad), Desktop = the panel's natural width. */}
              {previewMode && (
                <div className="inline-flex items-center rounded-md border border-slate-300 bg-white p-1" data-testid="viewport-switcher">
                  <button
                    type="button"
                    onClick={() => handleSwitchViewport('mobile')}
                    title="Mobile (375 px)"
                    data-testid="viewport-mobile"
                    className={`p-1.5 rounded transition-colors ${previewViewport === 'mobile' ? 'bg-indigo-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                  >
                    <Smartphone size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSwitchViewport('tablet')}
                    title="Tablet (768 px)"
                    data-testid="viewport-tablet"
                    className={`p-1.5 rounded transition-colors ${previewViewport === 'tablet' ? 'bg-indigo-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                  >
                    <Tablet size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSwitchViewport('desktop')}
                    title="Desktop (full width)"
                    data-testid="viewport-desktop"
                    className={`p-1.5 rounded transition-colors ${previewViewport === 'desktop' ? 'bg-indigo-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                  >
                    <Monitor size={16} />
                  </button>
                </div>
              )}

              {/* Full Page Preview */}
              <Button
                variant="outline"
                onClick={openFullPreview}
                data-testid="full-preview-button"
                title="Open full preview in new tab"
              >
                <ExternalLink size={18} className="mr-2" />
                Full Preview
              </Button>
              
              <Select
                value={pageData.status}
                onValueChange={(value) => updatePageData({ ...pageData, status: value })}
              >
                <SelectTrigger className="w-32" data-testid="status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                </SelectContent>
              </Select>
              
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="save-button"
              >
                <Save size={18} className="mr-2" />
                {saving ? 'Saving...' : 'Save'}
              </Button>

              <div className="w-px h-6 bg-slate-200 mx-1" />
              <Button
                onClick={logout}
                variant="ghost"
                className="text-slate-500 hover:text-red-600 hover:bg-red-50"
                data-testid="editor-logout-button"
                title="Logout"
              >
                <LogOut size={18} className="mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>

        {/* Title and Slug Section */}
        <div className="border-b border-slate-100 px-6 py-6 bg-slate-50">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="page-title" className="text-sm font-medium text-slate-600">
                  Page Title <span className="text-slate-400">(optional)</span>
                </Label>
                <Input
                  id="page-title"
                  value={pageData.title}
                  onChange={(e) => updatePageData({ ...pageData, title: e.target.value })}
                  placeholder="Enter page title..."
                  className="mt-1 text-xl font-semibold"
                  data-testid="page-title-input"
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                />
              </div>
              <div>
                <Label htmlFor="page-width" className="text-sm font-medium text-slate-600">
                  Page Width
                </Label>
                <Select
                  value={pageData.pageWidth || 'standard'}
                  onValueChange={(value) => updatePageData({ ...pageData, pageWidth: value })}
                >
                  <SelectTrigger className="mt-1" data-testid="page-width-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="narrow">Narrow (720px) - Best for reading</SelectItem>
                    <SelectItem value="standard">Standard (1024px) - Default</SelectItem>
                    <SelectItem value="wide">Wide (1280px) - More visual</SelectItem>
                    <SelectItem value="full">Full Width - Edge to edge</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Popup Page Toggle */}
            <div className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-lg">
              <div>
                <Label className="text-sm font-medium text-slate-600">Popup Page</Label>
                <p className="text-xs text-slate-500 mt-0.5">
                  Opens in a modal overlay without header/footer navigation
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={pageData.isPopup || false}
                  onChange={(e) => updatePageData({ ...pageData, isPopup: e.target.checked })}
                  className="sr-only peer"
                  data-testid="popup-page-toggle"
                />
                <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
              </label>
            </div>

            {/* Set as Homepage Toggle */}
            <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
              <div>
                <Label className="text-sm font-medium text-green-800">Set as Homepage</Label>
                <p className="text-xs text-green-600 mt-1">
                  This page will show at the root URL (/) of your site
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={pageData.isHomepage || false}
                  onChange={(e) => updatePageData({ ...pageData, isHomepage: e.target.checked })}
                  className="sr-only peer"
                  data-testid="homepage-toggle"
                />
                <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
              </label>
            </div>
            <div>
              <Label htmlFor="page-slug" className="text-sm font-medium text-slate-600">
                URL Slug <span className="text-red-500">*</span>
              </Label>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-slate-500">/</span>
                <Input
                  id="page-slug"
                  value={pageData.slug}
                  onChange={(e) => updatePageData({ ...pageData, slug: generateSlug(e.target.value) })}
                  placeholder="page-url-slug"
                  className="flex-1"
                  data-testid="page-slug-input"
                />
                {pageData.title && !pageData.slug && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => updatePageData({ ...pageData, slug: generateSlug(pageData.title) })}
                  >
                    Generate from title
                  </Button>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-1">
                This will be the URL path for your page
              </p>
            </div>

            {/* Parent Page Selector for Hierarchical URLs */}
            <div>
              <Label htmlFor="parent-page" className="text-sm font-medium text-slate-600">
                Parent Page <span className="text-slate-400">(optional)</span>
              </Label>
              <Select 
                value={pageData.parent_id || "none"} 
                onValueChange={(value) => updatePageData({ ...pageData, parent_id: value === "none" ? null : value })}
              >
                <SelectTrigger className="mt-1" data-testid="parent-page-select">
                  <SelectValue placeholder="No parent (root level page)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No parent (root level page)</SelectItem>
                  {availableParentPages.map(p => (
                    <SelectItem key={p.page_id} value={p.page_id}>
                      {p.full_path ? `/${p.full_path}` : `/${p.slug}`} - {p.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {pageData.parent_id && pageData.slug && (
                <p className="text-xs text-emerald-600 mt-1">
                  Full URL: /{availableParentPages.find(p => p.page_id === pageData.parent_id)?.full_path || ''}/{pageData.slug}
                </p>
              )}
              <p className="text-xs text-slate-500 mt-1">
                Select a parent to create hierarchical URLs (e.g., /about/team)
              </p>
            </div>

            {/* Page Theme Panel */}
            <PageThemePanel
              theme={pageData.theme}
              onChange={(theme) => updatePageData({ ...pageData, theme })}
            />

            {/* SEO & Social Sharing Panel */}
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setShowSeoPanel(!showSeoPanel)}
                className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Search size={18} className="text-slate-600" />
                  <span className="font-medium text-slate-700">SEO & Social Sharing</span>
                  {(pageData.meta_description || pageData.og_image || pageData.noindex) && (
                    <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                      Configured
                    </span>
                  )}
                </div>
                {showSeoPanel ? (
                  <ChevronUp size={18} className="text-slate-400" />
                ) : (
                  <ChevronDown size={18} className="text-slate-400" />
                )}
              </button>
              
              {showSeoPanel && (
                <div className="p-4 space-y-4 bg-white">
                  {/* Meta Description */}
                  <div>
                    <Label htmlFor="meta-description" className="text-sm font-medium text-slate-700">
                      Meta Description
                    </Label>
                    <Textarea
                      id="meta-description"
                      value={pageData.meta_description || ''}
                      onChange={(e) => updatePageData({ ...pageData, meta_description: e.target.value })}
                      placeholder="Brief description for search results (150-160 characters recommended)"
                      className="mt-1 h-20 resize-none"
                      maxLength={320}
                      data-testid="meta-description-input"
                    />
                    <p className={`text-xs mt-1 ${
                      (pageData.meta_description?.length || 0) > 160 
                        ? 'text-amber-600' 
                        : 'text-slate-500'
                    }`}>
                      {pageData.meta_description?.length || 0}/160 characters
                      {(pageData.meta_description?.length || 0) > 160 && ' (may be truncated in search results)'}
                    </p>
                  </div>

                  {/* Social Preview Image */}
                  <div>
                    <Label className="text-sm font-medium text-slate-700">
                      Social Preview Image
                    </Label>
                    <p className="text-xs text-slate-500 mb-2">
                      Image shown when page is shared on Facebook, Twitter, LinkedIn
                    </p>
                    {pageData.og_image ? (
                      <div className="relative">
                        <img
                          src={pageData.og_image}
                          alt="Social preview"
                          className="w-full max-w-md h-40 object-cover rounded-lg border border-slate-200"
                        />
                        <div className="absolute top-2 right-2 flex gap-1">
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={() => setShowOgImagePicker(true)}
                            className="h-7 px-2 bg-white/90 hover:bg-white text-xs"
                            data-testid="og-image-change-btn"
                          >
                            Change
                          </Button>
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={() => updatePageData({ ...pageData, og_image: '' })}
                            className="h-7 w-7 p-0 bg-white/90 hover:bg-white text-red-500"
                            title="Remove image"
                          >
                            <X size={14} />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setShowOgImagePicker(true)}
                        className="w-full max-w-md h-32 border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center gap-2 hover:border-indigo-400 hover:bg-indigo-50/50 transition-colors"
                        data-testid="og-image-picker-btn"
                      >
                        <ImageIcon size={24} className="text-slate-400" />
                        <span className="text-xs text-slate-500">Choose from Media Library</span>
                        <span className="text-[11px] text-slate-400">Recommended: 1200×630 pixels</span>
                      </button>
                    )}
                  </div>

                  {/* Noindex Toggle */}
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <Label className="text-sm font-medium text-slate-700">
                        Hide from Search Engines
                      </Label>
                      <p className="text-xs text-slate-500">
                        Adds "noindex" tag to prevent Google from indexing this page
                      </p>
                    </div>
                    <label className="relative inline-flex cursor-pointer">
                      <input
                        type="checkbox"
                        checked={pageData.noindex || false}
                        onChange={(e) => updatePageData({ ...pageData, noindex: e.target.checked })}
                        className="sr-only peer"
                        data-testid="noindex-toggle"
                      />
                      <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
                    </label>
                  </div>

                  {/* Advanced SEO Settings (collapsed by default) */}
                  <details className="group">
                    <summary className="cursor-pointer text-sm text-slate-600 hover:text-slate-800 list-none flex items-center gap-1">
                      <ChevronDown size={14} className="group-open:rotate-180 transition-transform" />
                      Advanced Settings
                    </summary>
                    <div className="mt-3 space-y-3 pl-5 border-l-2 border-slate-200">
                      {/* OG Title Override */}
                      <div>
                        <Label htmlFor="og-title" className="text-sm text-slate-600">
                          Social Title Override
                        </Label>
                        <Input
                          id="og-title"
                          value={pageData.og_title || ''}
                          onChange={(e) => updatePageData({ ...pageData, og_title: e.target.value })}
                          placeholder={pageData.title || 'Uses page title by default'}
                          className="mt-1 text-sm"
                          data-testid="og-title-input"
                        />
                      </div>

                      {/* OG Description Override */}
                      <div>
                        <Label htmlFor="og-description" className="text-sm text-slate-600">
                          Social Description Override
                        </Label>
                        <Textarea
                          id="og-description"
                          value={pageData.og_description || ''}
                          onChange={(e) => updatePageData({ ...pageData, og_description: e.target.value })}
                          placeholder={pageData.meta_description || 'Uses meta description by default'}
                          className="mt-1 h-16 text-sm resize-none"
                          data-testid="og-description-input"
                        />
                      </div>

                      {/* Canonical URL */}
                      <div>
                        <Label htmlFor="canonical-url" className="text-sm text-slate-600">
                          Canonical URL
                        </Label>
                        <Input
                          id="canonical-url"
                          value={pageData.canonical_url || ''}
                          onChange={(e) => updatePageData({ ...pageData, canonical_url: e.target.value })}
                          placeholder="Leave empty to use default page URL"
                          className="mt-1 text-sm"
                          data-testid="canonical-url-input"
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          Use if this content exists on another URL
                        </p>
                      </div>
                    </div>
                  </details>

                  {/* Social Preview — live mock cards showing how this page
                      will render when shared on Facebook, LinkedIn, and Twitter/X.
                      Mirrors the backend OG fallback chain:
                        page.og_image → page meta → tenant logo → tenant favicon */}
                  <div className="pt-4 border-t border-slate-200">
                    <h4 className="text-sm font-medium text-slate-700 mb-3">Social preview</h4>
                    <SocialPreview
                      title={pageData.og_title || pageData.title}
                      description={pageData.og_description || pageData.meta_description}
                      image={pageData.og_image || firstBlockImage(pageData.blocks) || tenantBranding?.logo_url || tenantBranding?.favicon_url || ''}
                      url={pageData.canonical_url || `https://${typeof window !== 'undefined' ? window.location.host : ''}/${pageData.slug || ''}`}
                      siteName={user?.tenant_name || ''}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="px-6 pb-20">
          {previewMode ? (
            (() => {
              // Desktop: inline render — fast, no save needed.
              if (previewViewport === 'desktop') {
                return (
                  <PreviewRenderer 
                    blocks={pageData.blocks} 
                    title={pageData.title} 
                    pageWidth={pageData.pageWidth} 
                    theme={pageData.theme}
                    siteTheme={siteTheme}
                    pageId={id}
                    tenantId={user?.tenant_id}
                    showNavigation={!isNew}
                    refreshKey={previewRefreshKey}
                    isQuickPreview={true}
                  />
                );
              }
              // Mobile / tablet: render inside an iframe pointing to the public
              // /preview/<token> page. This is the only way to get a truly
              // narrow viewport inside the editor — Tailwind's `md:` breakpoints
              // and CSS `vw` units measure the iframe's own viewport, so blocks
              // stack and font clamp()s shrink exactly as they will on a phone.
              const viewportWidth = previewViewport === 'mobile' ? 375 : 768;
              const viewportHeight = previewViewport === 'mobile' ? 812 : 1024;
              return (
                <div className="flex flex-col items-center" data-testid={`preview-frame-${previewViewport}`}>
                  <div className="text-xs text-slate-500 mb-3 flex items-center gap-2">
                    <span>{previewViewport === 'mobile' ? 'iPhone' : 'iPad'} preview · {viewportWidth} × {viewportHeight}</span>
                    <button
                      type="button"
                      onClick={refreshIframePreview}
                      className="underline hover:text-indigo-600"
                      data-testid="preview-iframe-refresh"
                    >
                      refresh
                    </button>
                  </div>
                  <div
                    className="bg-white shadow-xl border-[10px] border-slate-800 rounded-[36px] overflow-hidden"
                    style={{ width: `${viewportWidth + 20}px`, maxWidth: '100%' }}
                  >
                    {iframeLoading || !previewIframeUrl ? (
                      <div className="flex items-center justify-center text-slate-400 text-sm" style={{ width: `${viewportWidth}px`, height: `${viewportHeight}px`, maxWidth: '100%' }}>
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mr-2"></div>
                        Loading preview…
                      </div>
                    ) : (
                      <iframe
                        title="Mobile preview"
                        src={previewIframeUrl}
                        style={{
                          width: `${viewportWidth}px`,
                          height: `${viewportHeight}px`,
                          maxWidth: '100%',
                          border: 0,
                          display: 'block',
                        }}
                        data-testid="preview-iframe"
                      />
                    )}
                  </div>
                </div>
              );
            })()
          ) : (
            <BlockEditor
              blocks={pageData.blocks}
              onChange={(blocks) => updatePageData({ ...pageData, blocks })}
              pages={allPages}
              allPagesAnchors={allPagesAnchors}
              siteTheme={siteTheme}
            />
          )}
        </div>
      </div>

      {/* AI Designer Modal */}
      <AIDesignerModal
        open={showAIDesigner}
        onOpenChange={setShowAIDesigner}
        onBlocksGenerated={handleAIBlocksGenerated}
      />

      {/* Save as Template Modal */}
      <SaveAsTemplateModal
        open={showSaveAsTemplate}
        onClose={() => setShowSaveAsTemplate(false)}
        pageData={pageData}
        onSaved={() => {}}
      />

      {/* Template Selection Modal (for new pages) */}
      <TemplateSelectionModal
        open={showTemplateSelection}
        onClose={() => {
          setShowTemplateSelection(false);
          // Mark initial load complete for blank pages
          setTimeout(() => {
            initialLoadComplete.current = true;
          }, 100);
        }}
        onSelectTemplate={handleSelectTemplate}
        onStartBlank={() => {
          // Mark initial load complete for blank pages
          setTimeout(() => {
            initialLoadComplete.current = true;
          }, 100);
        }}
      />

      {/* Unsaved Changes Warning Modal */}
      <Dialog open={showUnsavedModal} onOpenChange={setShowUnsavedModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle size={20} />
              Unsaved Changes
            </DialogTitle>
            <DialogDescription>
              You have unsaved changes on this page. What would you like to do?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-600">
              If you leave without saving, your changes will be lost.
            </p>
          </div>
          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={handleCancelNavigation} className="w-full sm:w-auto">
              Stay on Page
            </Button>
            <Button variant="destructive" onClick={handleDiscardChanges} className="w-full sm:w-auto">
              Discard Changes
            </Button>
            <Button onClick={handleSaveAndNavigate} className="bg-indigo-600 hover:bg-indigo-700 w-full sm:w-auto">
              <Save size={16} className="mr-2" />
              Save & Leave
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Social Preview Image Picker */}
      <MediaPickerModal
        open={showOgImagePicker}
        onClose={() => setShowOgImagePicker(false)}
        onSelect={(media) => {
          updatePageData({ ...pageData, og_image: media.url });
          setShowOgImagePicker(false);
        }}
      />
    </div>
  );
};

export default PageEditor;
